import { useState, useEffect, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DeliveryBadge } from "@/components/DeliveryBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Combobox } from "@/components/ui/combobox";
import { DateField } from "@/components/ui/date-field";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Sparkles, FileText, Copy, MessageSquare, Mail, Phone, Euro, StickyNote, ShieldAlert,
  Plus, User, Users2, CreditCard, Trash2, Bell, TrendingUp, CheckCircle2, ChevronDown, History, CalendarClock, FileStack, Banknote, Pencil, AlertTriangle, Download, Eye, MessageCircle, FolderPlus, FolderOpen, Archive, ArchiveRestore, ClipboardList, Upload, X, Scale,
} from "lucide-react";
import { toast } from "sonner";
import { fmtEur, fmtDate, monthLabel, LETTER_TYPES, photoSrc } from "@/lib/format";
import ReportDialog from "@/components/ReportDialog";
import NotifySettingsWizard from "@/components/wizards/NotifySettingsWizard";
import TopupWizard from "@/components/wizards/TopupWizard";
import TenantReminderDialog from "@/components/TenantReminderDialog";
import AddTermWizard from "@/components/wizards/AddTermWizard";
import TenantEditDialog from "@/components/TenantEditDialog";
import QuickMessageDialog from "@/components/QuickMessageDialog";
import DossierExportDialog from "@/components/DossierExportDialog";
import StatementDialog from "@/components/StatementDialog";
import InvoiceThumb from "@/components/InvoiceThumb";
import ContractWizard from "@/components/wizards/ContractWizard";
import FactoringWizard from "@/components/wizards/FactoringWizard";
import ProcessPaymentWizard from "@/components/wizards/ProcessPaymentWizard";
import FactoringDetailDialog from "@/components/FactoringDetailDialog";
import CallDetailDialog from "@/components/CallDetailDialog";
import { AddressMapButton } from "@/components/AddressMapButton";
import { DossierViewer } from "@/components/DossierViewer";
import { DaysOpenBadge, CLOSED_COMPLAINT } from "@/pages/Complaints";
import DossierWizard from "@/components/DossierWizard";
import DossierEditDialog from "@/components/DossierEditDialog";
import { DocumentsTab } from "@/components/DocumentsTab";
import { RemindButton } from "@/components/RemindButton";
import { ClientAgreements } from "@/components/ClientAgreements";
const STATUS = {
  paid_ontime: { l: "Betaald op tijd", c: "bg-emerald-100 text-emerald-700" },
  paid_late: { l: "Betaald (te laat)", c: "bg-amber-100 text-amber-700" },
  partial: { l: "Deels betaald", c: "bg-orange-100 text-orange-700" },
  open: { l: "Openstaand", c: "bg-slate-100 text-slate-600" },
  late: { l: "Te laat", c: "bg-red-100 text-red-700" },
};

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [payments, setPayments] = useState([]);
  const [letters, setLetters] = useState([]);
  const [incassoTraject, setIncassoTraject] = useState([]);
  const [calls, setCalls] = useState([]);
  const [callDetail, setCallDetail] = useState(null);
  const [commFilter, setCommFilter] = useState("alle");
  const [viewDossier, setViewDossier] = useState(null);
  const [editDossier, setEditDossier] = useState(null);
  const [fu, setFu] = useState({ date: "", note: "" });
  const [timeline, setTimeline] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [signDialog, setSignDialog] = useState({ open: false, mode: null, agreement: null });
  const [signEmails, setSignEmails] = useState([]);
  const [signExtra, setSignExtra] = useState("");
  const [msgDialog, setMsgDialog] = useState({ open: false, contacts: [] });
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierWizardOpen, setDossierWizardOpen] = useState(false);
  const [dossiers, setDossiers] = useState([]);
  const [hiddenMods, setHiddenMods] = useState([]);
  const noPay = hiddenMods.includes("tab-betalingen");
  const noContracts = hiddenMods.includes("contracts");
  const noFact = hiddenMods.includes("tab-factoring");
  const [progressDlg, setProgressDlg] = useState({ open: false, dossier: null, note: "", busy: false });
  const [emailDlg, setEmailDlg] = useState({ open: false, dossier: null, recipients: null, type: "tenant", email: "", message: "", sending: false });
  const openMsg = (contacts) => setMsgDialog({ open: true, contacts });
  const tenantContacts = () => {
    const arr = [];
    const p1 = [t.first_name, t.last_name].filter(Boolean).join(" ") || "Persoon 1";
    arr.push({ name: p1, role: "Persoon 1", phone: t.mobile1 || t.mobile2, email: t.email1 || t.email2 });
    if (t.mobile2 || t.email2) arr.push({ name: p1, role: "Persoon 1 (alt.)", phone: t.mobile2, email: t.email2 });
    const p2 = [t.first_name2, t.last_name2].filter(Boolean).join(" ") || t.partner_name;
    if (p2 || t.partner_mobile || t.partner_email) arr.push({ name: p2 || "Persoon 2 / partner", role: "Persoon 2 / partner", phone: t.partner_mobile, email: t.partner_email });
    [["employer", "Werkgever"], ["guarantor", "Garantsteller"], ["administrator", "Bewindvoerder"]].forEach(([bk, label]) => {
      const b = t[bk] || {};
      if (b.contact1_name || b.contact1_phone || b.contact1_email) arr.push({ name: b.contact1_name || b.name || label, role: [label, b.contact1_function].filter(Boolean).join(" · "), phone: b.contact1_phone, email: b.contact1_email });
      if (b.contact2_name || b.contact2_phone || b.contact2_email) arr.push({ name: b.contact2_name || b.name || label, role: [label, b.contact2_function].filter(Boolean).join(" · "), phone: b.contact2_phone, email: b.contact2_email });
      (b.contacts || []).forEach((ct) => (ct.name || ct.phone || ct.email) && arr.push({ name: ct.name || label, role: [label, ct.function].filter(Boolean).join(" · "), phone: ct.phone, email: ct.email }));
    });
    return arr;
  };
  const msgTemplates = () => {
    const naam = [t.first_name, t.last_name].filter(Boolean).join(" ") || "klant";
    const bedrag = fmtEur(t.outstanding);
    const fill = (s) => (s || "").replaceAll("{naam}", naam).replaceAll("{bedrag}", bedrag);
    return (catalog.message_templates || []).map((tpl) => ({ label: tpl.label, subject: fill(tpl.subject), body: fill(tpl.body) }));
  };
  const defaultMsgTemplate = () => {
    const tpls = msgTemplates();
    if (!tpls.length) return null;
    const find = (kw) => tpls.find((x) => (x.label || "").toLowerCase().includes(kw));
    if (t.dunning_stage === "aanmaning") return find("aanmaning") || tpls[2] || tpls[0];
    if (t.dunning_stage === "herinnering_2") return find("tweede") || find("2") || tpls[1] || tpls[0];
    if (t.dunning_stage === "herinnering_1") return find("herinnering") || tpls[0];
    return null;
  };
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupContract, setTopupContract] = useState(null);
  const [addTermOpen, setAddTermOpen] = useState(false);
  const [expandedPay, setExpandedPay] = useState(null);
  const [openQuarters, setOpenQuarters] = useState({});
  const [statementOpen, setStatementOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [factoringOpen, setFactoringOpen] = useState(false);
  const [factoring, setFactoring] = useState([]);
  const [factoringDetail, setFactoringDetail] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [processOpen, setProcessOpen] = useState(false);
  const [processPaymentId, setProcessPaymentId] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [call, setCall] = useState({ date: new Date().toISOString().slice(0, 10), type: "telefoon", action_type: "", outcome: "", agreements: "", attachments: [], contract_id: "", payment_id: "", factoring_id: "", followup_date: "" });
  const [uploadingShot, setUploadingShot] = useState(false);
  const [tab, setTab] = useState("profiel");
  const [catalog, setCatalog] = useState({ risk_profiles: [], debtor_statuses: [], action_types: [] });
  const [newAction, setNewAction] = useState("");
  const addActionType = () => {
    const a = newAction.trim();
    if (!a) return;
    if (!(catalog.action_types || []).includes(a)) {
      const next = [...(catalog.action_types || []), a];
      setCatalog((c) => ({ ...c, action_types: next }));
      api.put("/catalog", { action_types: next }).catch(() => {});
    }
    setCall((cl) => ({ ...cl, action_type: a }));
    setNewAction("");
  };

  const startNoteForPayment = (p) => {
    setCall({ date: new Date().toISOString().slice(0, 10), type: "notitie", action_type: "", outcome: "", agreements: "", attachments: [], contract_id: p.contract_id || "", payment_id: p.id, factoring_id: "", followup_date: "" });
    setTab("gesprekken");
    toast.info(`Notitie gestart voor ${monthLabel(p.period)}${p.contract_ovk ? ` · ${p.contract_ovk}` : ""}`);
    setTimeout(() => document.querySelector("[data-testid='call-outcome-input']")?.focus(), 300);
  };

  const startPaymentForRow = (p) => {
    setProcessPaymentId(p.id);
    setProcessOpen(true);
  };

  const load = useCallback(async () => {
    const [tt, pp, ll, cc, tl, ff, ag, it] = await Promise.all([
      api.get(`/tenants/${id}`), api.get(`/tenants/${id}/payments`), api.get(`/tenants/${id}/letters`),
      api.get(`/tenants/${id}/calllogs`), api.get(`/tenants/${id}/timeline`), api.get(`/factoring?tenant_id=${id}`),
      api.get(`/agreements?tenant_id=${id}`), api.get(`/tenants/${id}/incasso-traject`),
    ]);
    setT(tt.data); setPayments(pp.data); setLetters(ll.data); setCalls(cc.data); setTimeline(tl.data); setFactoring(ff.data); setAgreements(ag.data); setIncassoTraject(it.data);
  }, [id]);

  const loadDossiers = useCallback(() => { api.get(`/tenants/${id}/dossiers`).then((r) => setDossiers(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { loadDossiers(); }, [loadDossiers]);
  useEffect(() => { api.get("/catalog").then((r) => setHiddenMods(r.data.hidden_modules || [])).catch(() => {}); }, []);

  const downloadDossierPdf = async (d) => {
    try {
      const res = await api.get(`/dossiers/${d.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `${d.number}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch { toast.error("Dossier-PDF openen mislukt"); }
  };
  const deleteDossier = async (d) => {
    try { await api.delete(`/dossiers/${d.id}`); toast.success(`Dossier ${d.number} verwijderd`); loadDossiers(); }
    catch { toast.error("Verwijderen mislukt"); }
  };
  const openProgress = (d) => { setFu({ date: "", note: "" }); setProgressDlg({ open: true, dossier: d, note: "", busy: false }); };
  const addDossierFollowup = async () => {
    const did = progressDlg.dossier.id;
    if (!fu.date) { toast.error("Kies een opvolgdatum"); return; }
    try { await api.post(`/dossiers/${did}/followup`, fu); setFu({ date: "", note: "" }); await refreshProgress(did); toast.success("Opvolgtaak gepland — zichtbaar onder Taken"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opvolgtaak plannen mislukt"); }
  };
  const refreshProgress = async (did) => {
    try { const r = await api.get(`/dossiers/${did}`); setProgressDlg((s) => ({ ...s, dossier: r.data })); loadDossiers(); load(); } catch {}
  };
  const addProgressNote = async () => {
    const did = progressDlg.dossier.id;
    if (!progressDlg.note.trim()) { toast.error("Notitie mag niet leeg zijn"); return; }
    setProgressDlg((s) => ({ ...s, busy: true }));
    try { await api.post(`/dossiers/${did}/entries`, { type: "note", text: progressDlg.note }); setProgressDlg((s) => ({ ...s, note: "" })); await refreshProgress(did); toast.success("Notitie toegevoegd"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Toevoegen mislukt"); }
    setProgressDlg((s) => ({ ...s, busy: false }));
  };
  const uploadProgressFiles = async (files) => {
    const did = progressDlg.dossier.id;
    setProgressDlg((s) => ({ ...s, busy: true }));
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); await api.post(`/dossiers/${did}/entries`, { type: "file", file_url: r.data.url, file_name: file.name }); }
      catch { toast.error(`Upload mislukt: ${file.name}`); }
    }
    await refreshProgress(did); toast.success("Bestand(en) toegevoegd");
    setProgressDlg((s) => ({ ...s, busy: false }));
  };
  const deleteProgressEntry = async (eid) => {
    const did = progressDlg.dossier.id;
    try { await api.delete(`/dossiers/${did}/entries/${eid}`); await refreshProgress(did); }
    catch { toast.error("Verwijderen mislukt"); }
  };
  const aiDossierEmail = async () => {
    setEmailDlg((s) => ({ ...s, aiBusy: true }));
    try { const r = await api.post(`/dossiers/${emailDlg.dossier.id}/ai-email`, { recipient_type: emailDlg.type }); setEmailDlg((s) => ({ ...s, message: r.data.message, aiBusy: false })); toast.success("AI-tekst ingevuld — pas gerust aan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI-tekst mislukt"); setEmailDlg((s) => ({ ...s, aiBusy: false })); }
  };
  const openEmailDlg = async (d) => {
    setEmailDlg({ open: true, dossier: d, recipients: null, type: "tenant", email: "", message: "", sending: false });
    try {
      const r = await api.get(`/dossiers/${d.id}/recipients`);
      const type = (r.data.tenant?.emails?.length ? "tenant" : (r.data.client?.emails?.length ? "client" : "tenant"));
      const email = (r.data[type]?.emails?.[0]) || "";
      setEmailDlg((s) => ({ ...s, recipients: r.data, type, email }));
    } catch { toast.error("Ontvangers laden mislukt"); }
  };
  const sendDossierEmail = async () => {
    if (!emailDlg.email) { toast.error("Kies een e-mailadres"); return; }
    setEmailDlg((s) => ({ ...s, sending: true }));
    try {
      const r = await api.post(`/dossiers/${emailDlg.dossier.id}/email`, { recipient_type: emailDlg.type, email: emailDlg.email, message: emailDlg.message });
      toast.success(`Dossier gemaild naar ${r.data.to}`);
      setEmailDlg((s) => ({ ...s, open: false, sending: false }));
      loadDossiers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verzenden mislukt"); setEmailDlg((s) => ({ ...s, sending: false })); }
  };

  // Afhandelen / archiveren / verwijderen — contracten (leningen) en facturen (factoring)
  const setContractHandled = async (c, val) => {
    try { await api.patch(`/contracts/${c.id}/handled`, { value: val }); toast.success(val ? "Gemarkeerd als afgehandeld" : "Afgehandeld ongedaan gemaakt"); load(); }
    catch { toast.error("Actie mislukt"); }
  };
  const archiveContract = async (c, val) => {
    try { await api.patch(`/contracts/${c.id}/archive`, { value: val }); toast.success(val ? "Naar archief verplaatst" : "Teruggezet uit archief"); load(); }
    catch { toast.error("Actie mislukt"); }
  };
  const deleteContract = async (c) => {
    try { const r = await api.delete(`/contracts/${c.id}`); toast.success(`Verwijderd (incl. ${r.data.deleted_payments} termijn(en))`); load(); }
    catch { toast.error("Verwijderen mislukt"); }
  };
  const setFactoringHandled = async (f, val) => {
    try { await api.patch(`/factoring/${f.id}/handled`, { value: val }); toast.success(val ? "Gemarkeerd als afgehandeld" : "Afgehandeld ongedaan gemaakt"); load(); }
    catch { toast.error("Actie mislukt"); }
  };
  const archiveFactoring = async (f, val) => {
    try { await api.patch(`/factoring/${f.id}/archive`, { value: val }); toast.success(val ? "Naar archief verplaatst" : "Teruggezet uit archief"); load(); }
    catch { toast.error("Actie mislukt"); }
  };
  const deleteFactoringItem = async (f) => {
    try { await api.delete(`/factoring/${f.id}`); toast.success("Factuur verwijderd"); load(); }
    catch { toast.error("Verwijderen mislukt"); }
  };
  const isContractDone = (c) => c.fully_paid || c.handled;
  const docStatus = (c) => {
    if (!c.document_only || c.auto_renew || !c.end_date) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const end = new Date(c.end_date); end.setHours(0, 0, 0, 0);
    const days = Math.round((end - today) / 86400000);
    if (days < 0) return { kind: "expired", days, label: `Verlopen op ${fmtDate(c.end_date)}`, badge: "bg-red-600 text-white hover:bg-red-600", text: "text-red-600" };
    if (days <= 30) return { kind: "soon", days, label: `Verloopt over ${days} dag${days === 1 ? "" : "en"}`, badge: "bg-amber-500 text-white hover:bg-amber-500", text: "text-amber-600" };
    return { kind: "ok", days, label: "", badge: "", text: "text-slate-800" };
  };
  const tabCnt = (n) => n > 0 ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-black/10 text-[10px] font-semibold px-1">{n}</span> : null;
  const archiveTenant = async () => {
    try { await api.patch(`/tenants/${id}/archive`, { value: true }); toast.success("Debiteur gearchiveerd"); navigate("/app/huurders"); }
    catch { toast.error("Archiveren mislukt"); }
  };
  const updateStatuses = async (next) => {
    setT((prev) => ({ ...prev, statuses: next }));
    try { await api.patch(`/tenants/${id}/statuses`, { statuses: next }); toast.success("Statussen bijgewerkt"); } catch { toast.error("Status bijwerken mislukt"); load(); }
  };
  const isFactoringDone = (f) => f.status === "paid" || f.handled;
  const activeContracts = () => contractList().filter((c) => !c.archived);
  const archivedContracts = () => contractList().filter((c) => c.archived);
  const activeFactoring = () => factoring.filter((f) => !f.archived);
  const archivedFactoring = () => factoring.filter((f) => f.archived);

  const createAndSendAgreement = async () => {
    openSignDialog("new");
  };
  const copyLink = (tok) => { navigator.clipboard.writeText(`${window.location.origin}/teken/${tok}`); toast.success("Link gekopieerd"); };
  const downloadAgreementPdf = async (a) => {
    if (a.uploaded_pdf_url) { window.open(photoSrc(a.uploaded_pdf_url), "_blank"); return; }
    try {
      const res = await api.get(`/agreements/${a.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `${a.status === "getekend" ? "overeenkomst" : "CONCEPT_overeenkomst"}_${(a.signer_name || tenant?.last_name || "debiteur").replace(/ /g, "_")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF genereren mislukt"); }
  };
  const downloadDossier = () => setDossierOpen(true);
  const commAll = [
    ...calls,
    ...letters.map((l) => ({ id: `letter-${l.id}`, type: "brief", date: l.created_at, action_type: LETTER_TYPES[l.type] || l.type, outcome: l.subject || "Brief", notes: l.body || "", recipients: l.recipients || [], _letter: l })),
  ].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  const COMM_LABELS = { telefoon: "Telefoon", email: "E-mail", sms: "SMS", whatsapp: "WhatsApp", notitie: "Notitie", huisbezoek: "Huisbezoek", brief: "Brief", document: "Document" };
  const commTypes = [["alle", "Alle", commAll.length], ...[...new Set(commAll.map((c) => c.type))].map((t) => [t, COMM_LABELS[t] || t, commAll.filter((c) => c.type === t).length])];
  const commShown = commFilter === "alle" ? commAll : commAll.filter((c) => c.type === commFilter);
  const openLetterPdf = async (l, download = false) => {
    try {
      const res = await api.get(`/letters/${l.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (download) {
        const link = document.createElement("a");
        link.href = url; link.download = `brief_${l.type || "brief"}_${(l.subject || "").slice(0, 24).replace(/[^a-z0-9]+/gi, "_")}.pdf`;
        document.body.appendChild(link); link.click(); link.remove();
      } else { window.open(url, "_blank"); }
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch { toast.error("Brief-PDF genereren mislukt"); }
  };
  const uploadSigned = async (aid, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/agreements/${aid}/upload-signed`, { url: up.data.url });
      toast.success("Getekende PDF opgeslagen"); load();
    } catch { toast.error("Upload mislukt"); }
  };

  useEffect(() => { load().catch(() => navigate("/app/huurders")); }, [load, navigate]);
  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const setStatus = async (p, status) => {
    const paid_date = (status === "paid_ontime" || status === "paid_late") ? new Date().toISOString().slice(0, 10) : "";
    await api.put(`/payments/${p.id}`, { ...p, status, paid_date });
    load();
  };

  const addCall = async () => {
    if (!call.outcome.trim() && !(call.attachments || []).length) return toast.error("Vul een uitkomst / notitie in of voeg een screenshot toe");
    await api.post("/calllogs", { tenant_id: id, ...call });
    toast.success("Gesprek toegevoegd");
    setCall({ date: new Date().toISOString().slice(0, 10), type: "telefoon", action_type: "", outcome: "", agreements: "", attachments: [], contract_id: "", payment_id: "", factoring_id: "", followup_date: "" });
    load();
  };

  const uploadShot = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingShot(true);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCall((c) => ({ ...c, attachments: [...(c.attachments || []), r.data.url] }));
      toast.success("Bijlage toegevoegd");
    } catch (e) { toast.error("Upload mislukt"); }
    finally { setUploadingShot(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Gekopieerd"); };
  const tlIcon = (k) => k === "call" ? Phone : k === "letter" ? Mail : k === "payment" ? Euro : k === "payout" ? Banknote : k === "complaint" ? ShieldAlert : k === "document" ? FileText : StickyNote;
  const contractList = () => (t?.contracts || (t?.contract ? [t.contract] : []));
  const contractLabel = (cid) => {
    const c = contractList().find((x) => x.id === cid);
    if (!c) return "—";
    const k = { huur: "Huur", lening: "Lening", factuur: "Factuur" }[c.kind] || c.kind;
    return c.ovk_number ? `${k} · ${c.ovk_number}` : k;
  };
  const agreementFor = (cid) => agreements.find((a) => a.contract_id === cid);
  const openAgreement = (a) => window.open(a.uploaded_pdf_url ? photoSrc(a.uploaded_pdf_url) : `/teken/${a.token}`, "_blank");
  const sendAgreement = async (a) => openSignDialog("existing", a);
  const loanContracts = () => contractList().filter((c) => c.kind === "lening" && !c.document_only);
  const unsignedLoans = () => loanContracts().filter((c) => { const a = agreementFor(c.id); return !a || a.status !== "getekend"; });
  const agreementStatusFor = (cid) => {
    const a = agreementFor(cid);
    if (a && a.status === "getekend") return { l: "Getekend", c: "bg-emerald-100 text-emerald-700", icon: "check" };
    if (a && a.status === "verzonden") return { l: "Verzonden — wacht op handtekening", c: "bg-blue-100 text-blue-700", icon: "clock" };
    return { l: "Nog te ondertekenen", c: "bg-amber-100 text-amber-700", icon: "warn" };
  };
  const handleSignAction = () => {
    const c = unsignedLoans()[0];
    if (!c) return;
    const a = agreementFor(c.id);
    if (a) sendAgreement(a); else createAndSendAgreement();
  };

  const savedEmails = () => {
    const out = [];
    const push = (e) => { const v = (e || "").trim(); if (v && !out.includes(v)) out.push(v); };
    push(t?.email1); push(t?.email2); push(t?.partner_email);
    ["correspondence", "employer", "guarantor", "administrator"].forEach((bk) => { const b = t?.[bk] || {}; push(b.email1); push(b.email2); });
    return out;
  };
  const openSignDialog = (mode, agreement = null) => {
    const emails = savedEmails();
    setSignEmails(emails.slice(0, 1));
    setSignExtra("");
    setSignDialog({ open: true, mode, agreement });
  };
  const toggleSignEmail = (e) => setSignEmails((cur) => cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]);
  const confirmSign = async () => {
    const emails = [...signEmails, ...signExtra.split(",").map((s) => s.trim()).filter(Boolean)];
    if (emails.length === 0) { toast.error("Kies of typ minstens één e-mailadres"); return; }
    try {
      let aid = signDialog.agreement?.id;
      if (signDialog.mode === "new") {
        const r = await api.post("/agreements", { tenant_id: id });
        aid = r.data.id;
      }
      const res = await api.post(`/agreements/${aid}/send`, { emails, split: true });
      toast.success(`Ondertekenlink verstuurd naar ${(res.data.recipients || emails).join(", ")}`);
      setSignDialog({ open: false, mode: null, agreement: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };

  const renderPayRow = (p) => (
    <Fragment key={p.id}>
      <TableRow data-testid={`payment-row-${p.id}`}>
        <TableCell>{(() => { const a = agreementFor(p.contract_id); return a ? <button onClick={() => openAgreement(a)} className="inline-flex" data-testid={`payment-agreement-${p.id}`}><Badge variant="outline" className="cursor-pointer hover:bg-blue-50 hover:border-blue-300"><FileText className="h-3 w-3 mr-1" />{contractLabel(p.contract_id)}</Badge></button> : <Badge variant="outline">{contractLabel(p.contract_id)}</Badge>; })()}</TableCell>
        <TableCell className="font-medium">{monthLabel(p.period)}</TableCell>
        <TableCell>{fmtDate(p.due_date)}{p.followup_date && <div className="text-xs text-blue-600 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> opvolg {fmtDate(p.followup_date)}</div>}</TableCell>
        <TableCell>{fmtEur(p.amount)}</TableCell>
        <TableCell className={p.penalty > 0 ? "text-red-600 font-medium" : ""}>{p.penalty > 0 ? fmtEur(p.penalty) : (p.grace_days ? <span className="text-xs text-slate-400">grace {p.grace_days}d</span> : "—")}</TableCell>
        <TableCell><Badge className={`${STATUS[p.status].c} hover:${STATUS[p.status].c}`}>{STATUS[p.status].l}</Badge></TableCell>
        <TableCell>
          <div className="w-36"><Combobox testId={`payment-status-${p.id}`} value={p.status} onChange={(v) => setStatus(p, v)} options={Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.l }))} /></div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {["open", "late", "partial"].includes(p.status) && (
              <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Betaling verwerken voor deze termijn" onClick={() => startPaymentForRow(p)} data-testid={`payment-process-${p.id}`}>
                <Banknote className="h-4 w-4" />
              </Button>
            )}
            {["open", "late", "partial"].includes(p.status) && (
              <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Notitie voor deze termijn" onClick={() => startNoteForPayment(p)} data-testid={`payment-note-${p.id}`}>
                <StickyNote className="h-4 w-4" />
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setExpandedPay(expandedPay === p.id ? null : p.id)} data-testid={`payment-expand-${p.id}`}>
              <ChevronDown className={`h-4 w-4 transition-transform ${expandedPay === p.id ? "rotate-180" : ""}`} />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expandedPay === p.id && (
        <TableRow data-testid={`payment-detail-${p.id}`}>
          <TableCell colSpan={8} className="bg-slate-50">
            <div className="grid sm:grid-cols-2 gap-4 p-2">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Details</div>
                <div className="text-sm text-slate-600 space-y-1">
                  <div>Grace-periode: {p.grace_days || 0} dagen</div>
                  <div>Opvolgdatum: {p.followup_date ? fmtDate(p.followup_date) : "—"}</div>
                  {p.comments && <div>Opmerking: {p.comments}</div>}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><History className="h-3 w-3" /> Audit trail</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {(p.audit || []).slice().reverse().map((a, i) => (
                    <div key={i} className="text-xs text-slate-500">{fmtDate(a.at)} — {a.action}</div>
                  ))}
                  {(p.audit || []).length === 0 && <div className="text-xs text-slate-400">Geen registraties.</div>}
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );

  const groupPayments = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const soonStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const soonEnd = new Date(today.getFullYear(), today.getMonth() + 3, 0);
    const sorted = [...payments].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    const soon = []; const groups = {};
    sorted.forEach((p) => {
      const d = new Date(p.due_date);
      if (d >= soonStart && d <= soonEnd) { soon.push(p); return; }
      const q = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
      (groups[q] = groups[q] || []).push(p);
    });
    const quarters = Object.entries(groups).map(([key, rows]) => ({
      key, rows, total: rows.reduce((s, r) => s + (r.amount || 0), 0), lastDue: rows[rows.length - 1].due_date,
      label: `${key.split("-")[1]} ${key.split("-")[0]}`,
    })).sort((a, b) => a.key.localeCompare(b.key));
    return {
      soon,
      past: quarters.filter((q) => new Date(q.lastDue) < soonStart),
      future: quarters.filter((q) => new Date(q.lastDue) >= soonStart),
      allKeys: quarters.map((q) => q.key),
    };
  };

  if (!t) return <div className="py-20 text-center text-slate-400">Laden...</div>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/app/huurders")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Terug naar debiteuren
      </button>

      {/* Header */}
      <Card className="p-6 border-slate-200/80 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-[#0F1E3D] text-[#C9A227] ring-2 ring-[#C9A227]/50 flex items-center justify-center text-lg font-bold">
              {t.first_name?.[0]}{t.last_name?.[0]}
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">{t.first_name} {t.last_name}</h1>
              <p className="text-slate-500 text-sm">{t.naw_street} {t.naw_number}, {t.naw_postal} {t.naw_city} <AddressMapButton address={`${t.naw_street || ""} ${t.naw_number || ""}, ${t.naw_postal || ""} ${t.naw_city || ""}`} /></p>
              {t.client_name && <p className="text-xs text-slate-400">Opdrachtgever: {t.client_name}</p>}
              <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="tenant-status-badges">
                {t.dunning_stage && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100" data-testid="tenant-dunning-badge">{({ herinnering_1: "1e herinnering verstuurd", herinnering_2: "2e herinnering verstuurd", aanmaning: "Aanmaning verstuurd" }[t.dunning_stage] || t.dunning_stage)}{t.dunning_stage_at ? ` · ${fmtDate(t.dunning_stage_at)}` : ""}</Badge>}
                {t.risk_profile && (() => {
                  const p = (catalog.risk_profiles || []).find((x) => x.name === t.risk_profile);
                  const col = { green: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700" }[p?.color] || "bg-slate-100 text-slate-700";
                  return <Badge className={`${col} hover:${col}`} data-testid="tenant-risk-badge">Risico: {t.risk_profile}</Badge>;
                })()}
                {(t.statuses || []).map((s) => (
                  <Badge key={s} variant="outline" className="border-blue-200 text-blue-700 pr-1 gap-1" data-testid={`tenant-status-badge-${s}`}>
                    {s}
                    <button className="ml-0.5 rounded-full hover:bg-blue-100 p-0.5" title="Verwijderen" onClick={() => updateStatuses((t.statuses || []).filter((x) => x !== s))} data-testid={`tenant-status-remove-${s}`}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
                {(() => {
                  const avail = (catalog.debtor_statuses || []).filter((s) => !(t.statuses || []).includes(s));
                  if (avail.length === 0) return null;
                  return (
                    <div className="w-40"><Combobox testId="tenant-status-add-trigger" value="" onChange={(v) => updateStatuses([...(t.statuses || []), v])} placeholder="+ Status" searchPlaceholder="Zoek status…" options={avail.map((s) => ({ value: s, label: s }))} /></div>
                  );
                })()}
              </div>
            </div>
          </div>
          {!noPay && <div className="text-right lg:text-left">
            <div className="text-xs text-slate-400">Openstaand incl. boete</div>
            <div className={`text-2xl font-bold ${t.outstanding > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtEur(t.outstanding)}</div>
          </div>}
        </div>
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100" data-testid="tenant-action-buttons">
          {!hiddenMods.includes("btn-brievengenerator") && <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => navigate(`/app/brievengenerator?tenant=${id}`)} data-testid="open-letter-generator-button"><FileText className="h-4 w-4 mr-2" /> Brievengenerator</Button>}
          <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setReportOpen(true)} data-testid="open-report-button"><FileText className="h-4 w-4 mr-2" /> Rapport</Button>
          {!noContracts && <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setContractOpen(true)} data-testid="add-contract-button"><Plus className="h-4 w-4 mr-2" /> Overeenkomst</Button>}
          <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setNotifyOpen(true)} data-testid="open-notify-button"><Bell className="h-4 w-4 mr-2" /> Meldingen</Button>
          {!noPay && <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setReminderOpen(true)} data-testid="open-reminder-schedule-button"><CalendarClock className="h-4 w-4 mr-2" /> Herinneringen</Button>}
          <Button size="sm" className="bg-white text-[#0F1E3D] border border-[#C9A227]/70 hover:bg-[#C9A227]/10" onClick={() => setEditOpen(true)} data-testid="edit-tenant-button"><Pencil className="h-4 w-4 mr-2" /> Bewerken</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="archive-tenant-button"><Archive className="h-4 w-4 mr-2" /> Archiveren</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Debiteur archiveren?</AlertDialogTitle>
                <AlertDialogDescription>Deze debiteur verdwijnt uit de actieve lijsten en wordt bewaard in het centrale archief. U kunt hem daar terugzetten of definitief verwijderen.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={archiveTenant} data-testid="archive-tenant-confirm">Ja, archiveren</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        {unsignedLoans().length > 0 && (
          <Card className="p-4 mb-4 border-amber-300 bg-amber-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="loan-unsigned-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div className="text-sm text-amber-800">
                <b>Leningsovereenkomst nog niet getekend.</b> Er {unsignedLoans().length === 1 ? "is 1 lening die" : `zijn ${unsignedLoans().length} leningen die`} ondertekend moet{unsignedLoans().length === 1 ? "" : "en"} worden voordat de termijnen en uitbetaling actief worden.
              </div>
            </div>
            <Button size="sm" className="bg-[#C9A227] hover:bg-[#B8921F] text-[#0F1E3D] shrink-0" onClick={handleSignAction} data-testid="loan-send-sign-link"><Mail className="h-4 w-4 mr-2" /> Ondertekenlink versturen</Button>
          </Card>
        )}
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 [&>button]:text-slate-600 [&>button]:font-medium [&>button[data-state=active]]:bg-[#0F1E3D] [&>button[data-state=active]]:text-white [&>button[data-state=active]]:shadow-[inset_0_-2px_0_0_#C9A227]">
          <TabsTrigger value="profiel" data-testid="tab-profiel"><User className="h-4 w-4 mr-1.5" /> Profiel</TabsTrigger>
          {!hiddenMods.includes("tab-betalingen") && <TabsTrigger value="betalingen" data-testid="tab-betalingen"><CreditCard className="h-4 w-4 mr-1.5" /> Betalingen{tabCnt(payments.length)}</TabsTrigger>}
          {!hiddenMods.includes("tab-brieven") && <TabsTrigger value="brieven" data-testid="tab-brieven"><Mail className="h-4 w-4 mr-1.5" /> Brieven{tabCnt(letters.length)}</TabsTrigger>}
          {!hiddenMods.includes("tab-brieven") && <TabsTrigger value="incasso" data-testid="tab-incasso"><Scale className="h-4 w-4 mr-1.5" /> Incasso{tabCnt(incassoTraject.length)}</TabsTrigger>}
          {!hiddenMods.includes("tab-gesprekken") && <TabsTrigger value="gesprekken" data-testid="tab-gesprekken"><Phone className="h-4 w-4 mr-1.5" /> Communicatie{tabCnt(commAll.length)}</TabsTrigger>}
          {!hiddenMods.includes("tab-factoring") && <TabsTrigger value="factoring" data-testid="tab-factoring"><FileStack className="h-4 w-4 mr-1.5" /> Factoring{tabCnt(activeFactoring().length)}</TabsTrigger>}
          <TabsTrigger value="tijdslijn" data-testid="tab-tijdslijn"><StickyNote className="h-4 w-4 mr-1.5" /> Tijdslijn{tabCnt(timeline.length)}</TabsTrigger>
          {!hiddenMods.includes("tab-dossiers") && <TabsTrigger value="dossiers" data-testid="tab-dossiers"><FolderOpen className="h-4 w-4 mr-1.5" /> Dossiers{tabCnt(dossiers.length)}</TabsTrigger>}
          <TabsTrigger value="documenten" data-testid="tab-documenten"><FileText className="h-4 w-4 mr-1.5" /> Documenten</TabsTrigger>
          <TabsTrigger value="archief" data-testid="tab-archief"><Archive className="h-4 w-4 mr-1.5" /> Archief{tabCnt(archivedContracts().length + archivedFactoring().length)}</TabsTrigger>
        </TabsList>

        {/* PROFIEL */}
        <TabsContent value="profiel" className="mt-4 grid md:grid-cols-2 gap-5">
          <Card className="p-5 cursor-pointer transition-colors hover:border-blue-300 hover:bg-blue-50/30 group" onClick={() => setEditOpen(true)} data-testid="contact-card-editable" title="Klik om te bewerken">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Mail className="h-4 w-4" /> Contactgegevens
              <Button size="sm" className="ml-auto h-7 bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={(e) => { e.stopPropagation(); openMsg(tenantContacts()); }} data-testid="tenant-message-button"><MessageCircle className="h-3.5 w-3.5 mr-1" /> Bericht</Button>
              <Pencil className="h-3.5 w-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
            <div className="space-y-3 text-sm">
              {(() => {
                const p1 = `${t.first_name || ""} ${t.last_name || ""}`.trim();
                const p2 = `${t.first_name2 || ""} ${t.last_name2 || ""}`.trim() || t.name2 || "";
                const hasP2 = !!(p2 || t.email2 || t.mobile2);
                const Row = ({ label, value, copyable }) => (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">{label}: <b className="text-slate-700">{value}</b></span>
                    {copyable && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); copy(value); }}><Copy className="h-3.5 w-3.5" /></Button>}
                  </div>
                );
                return (
                  <>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="contact-person-1">
                      <div className="flex items-center gap-2 mb-1.5"><Badge className="bg-blue-600 text-white hover:bg-blue-600 text-[10px]">Persoon 1</Badge><span className="font-medium text-slate-800">{p1 || "—"}</span></div>
                      {t.email1 ? <Row label="E-mail" value={t.email1} copyable /> : <div className="text-slate-400 text-xs">Geen e-mail</div>}
                      {t.mobile1 ? <Row label="Telefoon" value={t.mobile1} copyable /> : <div className="text-slate-400 text-xs">Geen telefoon</div>}
                    </div>
                    {hasP2 && (
                      <div className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="contact-person-2">
                        <div className="flex items-center gap-2 mb-1.5"><Badge className="bg-indigo-500 text-white hover:bg-indigo-500 text-[10px]">Persoon 2</Badge><span className="font-medium text-slate-800">{p2 || "—"}</span></div>
                        {t.email2 ? <Row label="E-mail" value={t.email2} copyable /> : <div className="text-slate-400 text-xs">Geen e-mail</div>}
                        {t.mobile2 ? <Row label="Telefoon" value={t.mobile2} copyable /> : <div className="text-slate-400 text-xs">Geen telefoon</div>}
                      </div>
                    )}
                    {t.iban && <div className="text-slate-500 pt-1">IBAN: <b className="text-slate-700">{t.iban}</b></div>}
                    {!(t.email1 || t.email2 || t.mobile1 || t.mobile2 || t.iban) && <p className="text-slate-400">Nog geen contactgegevens — klik om toe te voegen.</p>}
                  </>
                );
              })()}
            </div>
          </Card>
          <Card className="p-5 cursor-pointer transition-colors hover:border-blue-300 hover:bg-blue-50/30 group" onClick={() => setEditOpen(true)} data-testid="partner-card-editable" title="Klik om te bewerken">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Users2 className="h-4 w-4" /> Partnergegevens <Pencil className="h-3.5 w-3.5 text-slate-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
            {(() => {
              const pName = [t.first_name2, t.last_name2].filter(Boolean).join(" ") || t.partner_name;
              return (pName || t.partner_email || t.partner_mobile || t.gender2) ? (
                <div className="space-y-1 text-sm text-slate-600">
                  {pName && <div>Naam: <b className="text-slate-700">{pName}</b> <Badge className="ml-1 bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">persoon 2</Badge></div>}
                  {t.gender2 && <div>Geslacht: {t.gender2}</div>}
                  {t.partner_email && <div>E-mail: {t.partner_email}</div>}
                  {t.partner_mobile && <div>Mobiel: {t.partner_mobile}</div>}
                </div>
              ) : <p className="text-sm text-slate-400">Geen partner-/persoon 2-gegevens — klik om toe te voegen.</p>;
            })()}
          </Card>
          {(() => {
            const blocks = [["correspondence", "Correspondentieadres"], ["employer", "Werkgever"], ["guarantor", "Garantsteller"], ["administrator", "Bewindvoerder"]];
            const hasBlock = (b) => b && Object.values(b).some((v) => v && String(v).trim());
            const activeBlocks = blocks.filter(([k]) => hasBlock(t[k]));
            const customs = (t.custom_fields || []).filter((f) => f.label || f.value);
            const person2 = [t.first_name2, t.last_name2].filter(Boolean).join(" ") || t.name2;
            const hasExtra = t.gender1 || person2 || t.gender2 || activeBlocks.length || customs.length;
            if (!hasExtra) return null;
            return (
              <Card className="p-5 md:col-span-2 cursor-pointer transition-colors hover:border-blue-300 hover:bg-blue-50/30 group" onClick={() => setEditOpen(true)} data-testid="tenant-stamgegevens" title="Klik om te bewerken">
                <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><User className="h-4 w-4" /> Aanvullende stamgegevens <Pencil className="h-3.5 w-3.5 text-slate-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
                {(t.gender1 || person2 || t.gender2) && (
                  <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm mb-3">
                    {t.gender1 && <div><div className="text-xs text-slate-400">Geslacht (Persoon 1)</div><div className="text-slate-800">{t.gender1}</div></div>}
                    {person2 && <div><div className="text-xs text-slate-400">Persoon 2</div><div className="text-slate-800">{person2}</div></div>}
                    {t.gender2 && <div><div className="text-xs text-slate-400">Geslacht (Persoon 2)</div><div className="text-slate-800">{t.gender2}</div></div>}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  {activeBlocks.map(([k, label]) => {
                    const b = t[k];
                    return (
                      <div key={k} className="border rounded-lg p-3 text-sm" data-testid={`tenant-block-${k}`}>
                        <div className="text-xs font-semibold text-slate-500 mb-1">{label}</div>
                        {b.name && <div className="font-medium text-slate-800">{b.name}</div>}
                        {(b.street || b.number || b.postal || b.city) && <div className="text-slate-600">{b.street} {b.number}{(b.postal || b.city) ? `, ${b.postal} ${b.city}` : ""} <AddressMapButton address={`${b.street || ""} ${b.number || ""}, ${b.postal || ""} ${b.city || ""}`} /></div>}
                        {(b.mobile1 || b.mobile2) && <div className="text-slate-500 text-xs">{[b.mobile1, b.mobile2].filter(Boolean).join(" · ")}</div>}
                        {(b.email1 || b.email2) && <div className="text-slate-500 text-xs">{[b.email1, b.email2].filter(Boolean).join(" · ")}</div>}
                      </div>
                    );
                  })}
                </div>
                {customs.length > 0 && (
                  <div className="mt-3 pt-3 border-t grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                    {customs.map((f, i) => <div key={i} data-testid={`tenant-custom-${i}`}><div className="text-xs text-slate-400">{f.label || "—"}</div><div className="text-slate-800">{f.value}</div></div>)}
                  </div>
                )}
              </Card>
            );
          })()}
          {!noContracts && <Card className="p-5 md:col-span-2">
            <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><FileText className="h-4 w-4" /> Overeenkomsten{noPay ? "" : " & Boeteregeling"}</h3>
            {(() => {
              const contracts = activeContracts();
              if (contracts.length === 0) return <p className="text-sm text-slate-400">Geen actieve overeenkomst of lening. {archivedContracts().length > 0 ? "Zie het tabblad Archief." : ""}</p>;
              const kindLabel = (k) => k === "lening" ? "Lening" : k === "factuur" ? "Factuur" : k === "overig" ? "Overig" : "Huur";
              return (
                <div className="space-y-4">
                  {contracts.map((c) => (
                    <div key={c.id} className="rounded-lg border p-4" data-testid={`contract-card-${c.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{kindLabel(c.kind)}</Badge>
                          {c.ovk_number && <span className="text-xs font-mono text-slate-400">{c.ovk_number}</span>}
                          {c.title && <span className="text-sm font-medium text-slate-700" data-testid={`contract-title-${c.id}`}>{c.title}</span>}
                          {c.kind === "lening" && !c.document_only && (() => { const s = agreementStatusFor(c.id); return <Badge className={`${s.c} hover:${s.c}`} data-testid={`loan-sign-status-${c.id}`}>{s.icon === "check" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : s.icon === "warn" ? <AlertTriangle className="h-3 w-3 mr-1" /> : <CalendarClock className="h-3 w-3 mr-1" />}{s.l}</Badge>; })()}
                          {c.kind === "lening" && !c.document_only && (() => { const a = agreementFor(c.id); return a && a.status === "getekend" ? <Button size="sm" variant="outline" className="h-6 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => downloadAgreementPdf(a)} data-testid={`open-signed-doc-${c.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Open getekend document</Button> : null; })()}
                          {c.document_only && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50" data-testid={`contract-doconly-badge-${c.id}`}><FileText className="h-3 w-3 mr-1" /> Alleen document</Badge>}
                          {(() => { const ds = docStatus(c); return ds && ds.kind !== "ok" ? <Badge className={ds.badge} data-testid={`contract-expiry-badge-${c.id}`}><AlertTriangle className="h-3 w-3 mr-1" /> {ds.label}</Badge> : null; })()}
                          {isContractDone(c) && <Badge className="bg-emerald-600 text-white hover:bg-emerald-600" data-testid={`contract-handled-badge-${c.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Afgehandeld</Badge>}
                        </div>
                        {c.kind === "lening" && !c.document_only && (
                          <Button size="sm" variant="outline" onClick={() => { setTopupContract(c); setTopupOpen(true); }} data-testid={`loan-topup-button-${c.id}`}><TrendingUp className="h-4 w-4 mr-2" /> Lening ophogen</Button>
                        )}
                      </div>
                      {c.document_only ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                          <div><div className="text-slate-400 text-xs">Ingangsdatum</div><div className="font-bold text-slate-800">{fmtDate(c.start_date)}</div></div>
                          <div><div className="text-slate-400 text-xs">Einddatum</div><div className={`font-bold ${docStatus(c)?.text || "text-slate-800"}`}>{c.auto_renew ? "Automatisch verlengd" : (c.end_date ? fmtDate(c.end_date) : "—")}</div></div>
                          <div className="flex items-end">{c.file_url ? <Button size="sm" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => window.open(c.file_url, "_blank")} data-testid={`contract-doc-view-${c.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Bekijk contract</Button> : <span className="text-xs text-slate-400">Geen document</span>}</div>
                        </div>
                      ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div><div className="text-slate-400 text-xs">Maandbedrag</div><div className="font-bold text-slate-800">{fmtEur(c.monthly_rent)}</div></div>
                        <div><div className="text-slate-400 text-xs">Boete/dag</div><div className="font-bold text-slate-800">{fmtEur(c.penalty_per_day)}</div></div>
                        <div><div className="text-slate-400 text-xs">Max boete/maand</div><div className="font-bold text-slate-800">{fmtEur(c.max_penalty_per_month)}</div></div>
                        <div><div className="text-slate-400 text-xs">Ingangsdatum</div><div className="font-bold text-slate-800">{fmtDate(c.start_date)}</div></div>
                        {c.kind === "lening" ? (
                          <>
                            <div><div className="text-slate-400 text-xs">Hoofdsom</div><div className="font-bold text-slate-800">{fmtEur(c.total_amount)}</div></div>
                            <div><div className="text-slate-400 text-xs">Looptijd / rente</div><div className="font-bold text-slate-800">{c.term_months} mnd · {c.interest_rate}%</div></div>
                            <div><div className="text-slate-400 text-xs">Einddatum</div><div className="font-bold text-slate-800">{fmtDate(c.end_date)}</div></div>
                          </>
                        ) : (
                          <div><div className="text-slate-400 text-xs">Duur</div><div className="font-bold text-slate-800">{c.indefinite ? "Onbepaald (rollend)" : "Bepaald"}</div></div>
                        )}
                      </div>
                      )}
                      {(c.amendments || []).length > 0 && (
                        <div className="mt-4 pt-3 border-t">
                          <div className="text-xs font-semibold text-slate-500 mb-2">Wijzigingen (ophogingen)</div>
                          <div className="space-y-1">
                            {c.amendments.map((a, i) => (
                              <div key={i} className="text-xs text-slate-600 flex justify-between" data-testid={`amendment-${c.id}-${i}`}>
                                <span>{fmtDate(a.date)}: +{fmtEur(a.amount)} → hoofdsom {fmtEur(a.new_principal)}</span>
                                <span>{a.remaining_months} mnd tot {fmtDate(a.new_end_date)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-2">
                        {c.fully_paid && !c.handled && <span className="text-xs text-emerald-600 mr-1">Alle termijnen zijn betaald.</span>}
                        {!c.handled ? (
                          <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => setContractHandled(c, true)} data-testid={`contract-mark-handled-${c.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" /> Markeer afgehandeld</Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setContractHandled(c, false)} data-testid={`contract-unmark-handled-${c.id}`}>Afgehandeld ongedaan maken</Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => archiveContract(c, true)} data-testid={`contract-archive-${c.id}`}><Archive className="h-4 w-4 mr-1.5" /> Naar archief</Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`contract-delete-${c.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> Verwijderen</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{kindLabel(c.kind)} verwijderen?</AlertDialogTitle>
                              <AlertDialogDescription>Deze overeenkomst én alle bijbehorende betaaltermijnen worden definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteContract(c)} data-testid={`contract-delete-confirm-${c.id}`}>Ja, verwijderen</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>}
        </TabsContent>

        {/* BETALINGEN */}
        <TabsContent value="betalingen" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Betalingstermijnen</h3>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => { setProcessPaymentId(null); setProcessOpen(true); }} data-testid="process-payment-open"><CheckCircle2 className="h-4 w-4 mr-2" /> Verwerk betaling</Button>
                <Button size="sm" variant="outline" onClick={() => setStatementOpen(true)} data-testid="open-statement-button"><FileText className="h-4 w-4 mr-2" /> Betalingsoverzicht</Button>
                <Button size="sm" variant="outline" onClick={() => setAddTermOpen(true)} data-testid="add-term-button"><Plus className="h-4 w-4 mr-2" /> Termijn toevoegen</Button>
              </div>
            </div>
            {payments.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Nog geen termijnen. Genereer termijnen op basis van het contract.</p>
            ) : (() => {
              const g = groupPayments();
              const hasGroups = g.past.length > 0 || g.future.length > 0;
              const allOpen = g.allKeys.length > 0 && g.allKeys.every((k) => openQuarters[k]);
              const toggleAll = () => setOpenQuarters(allOpen ? {} : Object.fromEntries(g.allKeys.map((k) => [k, true])));
              const quarterHeader = (q) => (
                <TableRow key={`h-${q.key}`} className="bg-slate-100 hover:bg-slate-100 cursor-pointer" onClick={() => setOpenQuarters((o) => ({ ...o, [q.key]: !o[q.key] }))} data-testid={`quarter-header-${q.key}`}>
                  <TableCell colSpan={8} className="py-2">
                    <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
                      <ChevronDown className={`h-4 w-4 transition-transform ${openQuarters[q.key] ? "rotate-180" : ""}`} />
                      {q.label} <span className="font-normal text-slate-400">· {q.rows.length} termijnen · {fmtEur(q.total)}</span>
                    </div>
                  </TableCell>
                </TableRow>
              );
              return (
                <>
                  {hasGroups && (
                    <div className="flex justify-end mb-2">
                      <Button size="sm" variant="ghost" className="text-slate-500" onClick={toggleAll} data-testid="expand-all-quarters">
                        {allOpen ? "Alles inklappen" : "Alles uitklappen"}
                      </Button>
                    </div>
                  )}
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Overeenkomst</TableHead><TableHead>Maand</TableHead><TableHead>Vervaldatum</TableHead><TableHead>Bedrag</TableHead>
                      <TableHead>Boete</TableHead><TableHead>Status</TableHead><TableHead>Actie</TableHead><TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {g.past.map((q) => (
                        <Fragment key={q.key}>{quarterHeader(q)}{openQuarters[q.key] && q.rows.map(renderPayRow)}</Fragment>
                      ))}
                      {g.soon.length > 0 && hasGroups && (
                        <TableRow className="bg-emerald-50 hover:bg-emerald-50" data-testid="soon-header">
                          <TableCell colSpan={8} className="py-2"><div className="flex items-center gap-2 font-semibold text-emerald-700 text-sm"><CalendarClock className="h-4 w-4" /> Komende 3 maanden</div></TableCell>
                        </TableRow>
                      )}
                      {g.soon.map(renderPayRow)}
                      {g.future.map((q) => (
                        <Fragment key={q.key}>{quarterHeader(q)}{openQuarters[q.key] && q.rows.map(renderPayRow)}</Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </>
              );
            })()}
          </Card>
          {activeFactoring().length > 0 && (
            <Card className="p-5 mt-4" data-testid="betalingen-factoring-section">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Banknote className="h-4 w-4" /> Facturen / uitbetalingen</h3>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Factuur</TableHead><TableHead>Datum</TableHead><TableHead>Te ontvangen</TableHead><TableHead>Uit te keren</TableHead><TableHead>Status</TableHead><TableHead>Kopie</TableHead><TableHead className="text-right">Acties</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {activeFactoring().map((f) => (
                    <TableRow key={f.id} data-testid={`betalingen-factoring-row-${f.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setFactoringDetail(f)}>
                      <TableCell className="font-medium">{f.invoice_number || "—"}</TableCell>
                      <TableCell>{fmtDate(f.invoice_date)}</TableCell>
                      <TableCell>{fmtEur(f.amount)}</TableCell>
                      <TableCell className="text-emerald-700 font-medium">{fmtEur(f.payout)}</TableCell>
                      <TableCell>
                        {isFactoringDone(f)
                          ? <Badge className="bg-emerald-600 text-white hover:bg-emerald-600" data-testid={`factoring-handled-badge-${f.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Afgehandeld</Badge>
                          : <Badge className={f.payout_done ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{f.payout_done ? "Uitbetaald" : "Openstaand"}</Badge>}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}><InvoiceThumb url={f.file_url} label="Bekijk" testid={`factoring-view-${f.id}`} /></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {!f.handled && f.status !== "paid" && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700" title="Markeer afgehandeld" onClick={() => setFactoringHandled(f, true)} data-testid={`factoring-mark-handled-${f.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" title="Naar archief" onClick={() => archiveFactoring(f, true)} data-testid={`factoring-archive-${f.id}`}><Archive className="h-4 w-4" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Verwijderen" data-testid={`factoring-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Factuur verwijderen?</AlertDialogTitle>
                                <AlertDialogDescription>Deze factoring-factuur wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteFactoringItem(f)} data-testid={`factoring-delete-confirm-${f.id}`}>Ja, verwijderen</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
          {contractList().some((cc) => cc.kind === "lening" && !cc.document_only) && (
            <Card className="p-5 mt-4" data-testid="agreements-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold flex items-center gap-2"><FileStack className="h-4 w-4" /> Leningsovereenkomsten</h3>
                <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={createAndSendAgreement} data-testid="create-agreement-button"><Plus className="h-4 w-4 mr-1" /> Opstellen & versturen</Button>
              </div>
              <div className="space-y-2">
                {agreements.length === 0 && <p className="text-sm text-slate-400">Nog geen overeenkomst opgesteld.</p>}
                {agreements.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border" data-testid={`agreement-row-${a.id}`}>
                    <div>
                      <div className="font-medium text-slate-800">{a.title}</div>
                      <div className="text-xs text-slate-500">
                        <Badge className={a.status === "getekend" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : a.status === "verzonden" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"}>{a.status}</Badge>
                        {a.signed_at && <span className="ml-2">Getekend {fmtDate(a.signed_at)} door {a.signer_name}</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      <RemindButton agreement={a} onDone={load} testId={`agreement-remind-${a.id}`} />
                      {a.status !== "getekend" && <>
                        <Button size="sm" variant="ghost" className="text-[#0F1E3D]" onClick={() => downloadAgreementPdf(a)} data-testid={`agreement-draft-pdf-${a.id}`}><FileText className="h-4 w-4 mr-1 text-[#C9A227]" /> Concept-PDF</Button>
                        <Button size="sm" variant="ghost" className="text-blue-700" onClick={() => copyLink(a.token)} data-testid={`agreement-copy-${a.id}`}><Copy className="h-4 w-4 mr-1" /> Link</Button>
                        <label className="text-xs text-slate-500 underline cursor-pointer px-2" data-testid={`agreement-upload-${a.id}`}>
                          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => uploadSigned(a.id, e.target.files?.[0])} /> Getekende PDF uploaden
                        </label>
                      </>}
                      {a.status === "getekend" && <>
                        <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => downloadAgreementPdf(a)} data-testid={`agreement-pdf-${a.id}`}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      </>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="brieven" className="mt-4 space-y-5">
          {letters.some((l) => l.auto_generated && /herinnering|aanmaning/i.test(l.type)) && (
            <Card className="p-5" data-testid="reminder-log-card">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Bell className="h-4 w-4" /> Automatische herinneringen — logboek</h3>
              <div className="space-y-2">
                {letters.filter((l) => l.auto_generated && /herinnering|aanmaning/i.test(l.type)).map((l) => (
                  <div key={`log-${l.id}`} className="flex items-center justify-between border-l-2 border-amber-400 bg-amber-50/40 rounded-r-md px-3 py-2" data-testid={`reminder-log-${l.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary">{LETTER_TYPES[l.type] || l.type}</Badge>
                      {l.period && <span className="text-xs text-slate-500">termijn {l.period}</span>}
                      {l.sent
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Verzonden</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Concept (nog niet verzonden)</Badge>}
                    </div>
                    <span className="text-xs text-slate-500">{l.sent && l.sent_at ? `Verzonden ${fmtDate(l.sent_at)}` : `Aangemaakt ${fmtDate(l.created_at)}`}{l.recipients?.length ? ` → ${l.recipients.join(", ")}` : ""}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Correspondentie</h3>
              <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => navigate(`/app/brievengenerator?tenant=${id}`)} data-testid="new-letter-button"><Sparkles className="h-4 w-4 mr-2" /> Nieuwe brief</Button>
            </div>
            {letters.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Nog geen brieven opgesteld.</p> : (
              <div className="space-y-3">
                {letters.map((l) => (
                  <div key={l.id} className="border rounded-lg p-4" data-testid={`letter-item-${l.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary">{LETTER_TYPES[l.type] || l.type}</Badge>
                        <span className="font-medium text-slate-800">{l.subject}</span>
                        {l.sent
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Verzonden</Badge>
                          : (l.channel === "email" && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Concept</Badge>)}
                        {l.auto_generated && <Badge variant="outline" className="text-xs">Auto</Badge>}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-400">{fmtDate(l.created_at)} • {l.channel}</span>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Openen" onClick={() => openLetterPdf(l, false)} data-testid={`letter-open-${l.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Download PDF" onClick={() => openLetterPdf(l, true)} data-testid={`letter-download-${l.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Kopieer tekst" onClick={() => copy(`${l.subject}\n\n${l.body}`)}><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                    {l.recipients?.length > 0 && <div className="text-xs text-slate-400 mt-1">Naar: {l.recipients.join(", ")}</div>}
                    <pre className="text-xs whitespace-pre-wrap font-sans text-slate-500 mt-2 max-h-32 overflow-y-auto">{l.body}</pre>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* INCASSO-TRAJECT */}
        <TabsContent value="incasso" className="mt-4">
          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-1">Incassotraject</h3>
            <p className="text-sm text-slate-400 mb-4">Alle incassobrieven met kenmerk, briefsoort en verzendstatus in één doorlopende tijdlijn.</p>
            {incassoTraject.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Nog geen incassobrieven verstuurd. Maak er een via de Brievengenerator (Vaste incassobrief).</p> : (
              <div className="relative" data-testid="incasso-traject-list">
                {incassoTraject.map((l, i) => (
                  <div key={l.id} className="flex gap-4 pb-6 last:pb-0" data-testid={`incasso-traject-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0"><Scale className="h-4 w-4" /></div>
                      {i < incassoTraject.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 mt-1" />}
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">{l.kenmerk || "—"}</Badge>
                        <span className="font-medium text-slate-800">{l.incasso_label}</span>
                        {l.sent
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Verzonden</Badge>
                          : <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100">Opgesteld</Badge>}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{fmtDate(l.created_at)} • {l.subject}</div>
                      {l.recipients?.length > 0 && <div className="text-xs text-slate-400 mt-0.5">Naar: {l.recipients.join(", ")}</div>}
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" className="h-7" onClick={() => openLetterPdf(l, false)} data-testid={`incasso-open-${l.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Openen</Button>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => openLetterPdf(l, true)} data-testid={`incasso-pdf-${l.id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* GESPREKKEN */}
        <TabsContent value="gesprekken" className="mt-4 grid md:grid-cols-5 gap-5">
          <Card className="p-5 md:col-span-2 h-fit">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" /> Gesprek / Note / Document</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Datum</Label><DateField testId="call-date-input" value={call.date} onChange={(v) => setCall({ ...call, date: v })} /></div>
                <div><Label>Type</Label>
                  <Combobox testId="call-type-select" value={call.type} onChange={(v) => setCall({ ...call, type: v })} options={[["telefoon", "Telefoongesprek"], ["notitie", "Notitie"], ["huisbezoek", "Huisbezoek"], ["sms", "SMS"], ["whatsapp", "WhatsApp"], ["email", "E-mail"], ["brief", "Brief (ontvangen/verstuurd)"], ["document", "Document"]].map(([v, l]) => ({ value: v, label: l }))} />
                </div>
              </div>
              <div>
                <Label>Actietype</Label>
                <div className="flex gap-2">
                  <Combobox testId="call-action-type-select" value={call.action_type || "none"} onChange={(v) => setCall({ ...call, action_type: v === "none" ? "" : v })} placeholder="— Geen —" searchPlaceholder="Zoek actietype…" options={[{ value: "none", label: "— Geen —" }, ...(catalog.action_types || []).map((a) => ({ value: a, label: a }))]} />
                  <Input value={newAction} onChange={(e) => setNewAction(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addActionType(); } }} placeholder="Nieuw actietype…" className="max-w-[200px]" data-testid="call-new-action-input" />
                  <Button type="button" variant="outline" onClick={addActionType} data-testid="call-new-action-add"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              <div><Label>Uitkomst / inhoud</Label><Textarea rows={3} value={call.outcome} onChange={(e) => setCall({ ...call, outcome: e.target.value })} data-testid="call-outcome-input" /></div>
              <div><Label>Gemaakte afspraken</Label><Textarea rows={2} value={call.agreements} onChange={(e) => setCall({ ...call, agreements: e.target.value })} data-testid="call-agreements-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                {!noPay && <div>
                  <Label>Koppel aan overeenkomst</Label>
                  <Combobox testId="call-contract-select" value={call.contract_id} onChange={(v) => setCall({ ...call, contract_id: v })} placeholder="— Geen —" searchPlaceholder="Zoek overeenkomst…" options={[{ value: "", label: "— Geen —" }, ...contractList().map((cc) => ({ value: cc.id, label: `${({ huur: "Huur", lening: "Lening", factuur: "Factuur" }[cc.kind] || cc.kind)}${cc.ovk_number ? ` · ${cc.ovk_number}` : ` · start ${cc.start_date}`}` }))]} />
                </div>}
                {!noFact && <div>
                  <Label>Koppel aan factoring-factuur</Label>
                  <Combobox testId="call-factoring-select" value={call.factoring_id} onChange={(v) => setCall({ ...call, factoring_id: v })} placeholder="— Geen —" searchPlaceholder="Zoek factuur…" options={[{ value: "", label: "— Geen —" }, ...factoring.map((f) => ({ value: f.id, label: `${f.invoice_number || "Factuur"} · ${fmtEur(f.amount)}` }))]} />
                </div>}
              </div>
              {!noPay && <div>
                <Label>Koppel aan betaaltermijn</Label>
                <Combobox testId="call-payment-select" value={call.payment_id} onChange={(v) => setCall({ ...call, payment_id: v })} placeholder="— Geen —" searchPlaceholder="Zoek termijn…" options={[{ value: "", label: "— Geen —" }, ...payments.map((p) => ({ value: p.id, label: `${monthLabel(p.period)} · ${fmtEur(p.amount)}${p.contract_ovk ? ` · ${p.contract_ovk}` : ""}` }))]} />
              </div>}
              <div><Label>Opvolgdatum (verschijnt in Taken)</Label><DateField testId="call-followup-input" value={call.followup_date} onChange={(v) => setCall({ ...call, followup_date: v })} /></div>
              <div>
                <Label>Bijlagen (brieven, documenten, screenshots)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(call.attachments || []).map((url, i) => (
                    <div key={i} className="relative h-16 w-16 rounded-lg border overflow-hidden group" data-testid={`call-attachment-${i}`}>
                      {/\.(png|jpe?g|gif|webp|heic|heif)$/i.test(url)
                        ? <img src={photoSrc(url)} alt="" className="h-full w-full object-cover" />
                        : <div className="h-full w-full flex flex-col items-center justify-center bg-slate-50 text-slate-500 p-1"><FileText className="h-5 w-5" /><span className="text-[9px] truncate w-full text-center">{decodeURIComponent(url.split("/").pop() || "")}</span></div>}
                      <button type="button" onClick={() => setCall((c) => ({ ...c, attachments: c.attachments.filter((_, j) => j !== i) }))}
                        className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full h-4 w-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100" data-testid={`call-attachment-remove-${i}`}>×</button>
                    </div>
                  ))}
                  <label className={`h-16 w-16 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer text-slate-400 hover:border-emerald-400 hover:text-emerald-500 ${uploadingShot ? "opacity-50 pointer-events-none" : ""}`} data-testid="call-attachment-upload">
                    <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.odt,.txt,.rtf,.eml,.msg" className="hidden" data-testid="call-attachment-input" onChange={(e) => { Array.from(e.target.files || []).forEach((f) => uploadShot(f)); e.target.value = ""; }} />
                    {uploadingShot ? <Sparkles className="h-4 w-4 animate-pulse" /> : <Plus className="h-5 w-5" />}
                  </label>
                </div>
              </div>
              <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={addCall} data-testid="tenant-call-log-submit">Toevoegen aan log</Button>
            </div>
          </Card>
          <Card className="p-5 md:col-span-3">
            <h3 className="font-heading font-semibold mb-3">Communicatie</h3>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="comm-filters">
              {commTypes.map(([v, l, n]) => (
                <button key={v} type="button" onClick={() => setCommFilter(v)} data-testid={`comm-filter-${v}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${commFilter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {l} <span className="opacity-60">{n}</span>
                </button>
              ))}
            </div>
            {commShown.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Nog geen communicatie vastgelegd.</p> : (
              <div className="space-y-3">
                {commShown.map((c) => (
                  <div key={c.id} className="border rounded-lg p-3 cursor-pointer hover:bg-slate-50 hover:border-slate-300 transition-colors" onClick={() => setCallDetail(c)} title="Klik om het hele bericht te bekijken" data-testid={`call-item-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="capitalize">{c.type}</Badge>
                      {c.action_type && <Badge className="ml-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-100" data-testid={`call-action-${c.id}`}>{c.action_type}</Badge>}
                      <DeliveryBadge status={c.delivery_status} channel={c.delivery_channel} testId={`call-delivery-${c.id}`} />
                      <span className="text-xs text-slate-400">{fmtDate(c.date)}</span>
                    </div>
                    {(c.contract_id || c.factoring_id || c.payment_id) && (
                      <div className="flex flex-wrap gap-1.5 mt-2" onClick={(e) => e.stopPropagation()} data-testid={`call-links-${c.id}`}>
                        {c.contract_id && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 font-normal"><FileText className="h-3 w-3 mr-1" />{contractLabel(c.contract_id)}</Badge>}
                        {c.factoring_id && (() => { const f = factoring.find((x) => x.id === c.factoring_id); return <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50 font-normal cursor-pointer" onClick={() => f && setFactoringDetail(f)} data-testid={`call-factoring-link-${c.id}`}><Banknote className="h-3 w-3 mr-1" />Factuur {f ? (f.invoice_number || fmtEur(f.amount)) : "—"}</Badge>; })()}
                        {c.payment_id && (() => { const p = payments.find((x) => x.id === c.payment_id); return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-normal"><Euro className="h-3 w-3 mr-1" />{p ? monthLabel(p.period) : "termijn"}</Badge>; })()}
                      </div>
                    )}
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{c.outcome}</p>
                    {c.notes && <p className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap" data-testid={`call-preview-${c.id}`}>{c.notes}</p>}
                    {c.agreements && <p className="text-xs text-slate-500 mt-1">Afspraken: {c.agreements}</p>}
                    {(c.attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                        {c.attachments.map((url, i) => (
                          /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(url) ? (
                            <a key={i} href={photoSrc(url)} target="_blank" rel="noreferrer" className="block h-20 w-20 rounded-lg border overflow-hidden" data-testid={`call-item-image-${c.id}-${i}`}>
                              <img src={photoSrc(url)} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform" />
                            </a>
                          ) : (
                            <a key={i} href={photoSrc(url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-slate-50 text-xs text-slate-700 hover:bg-slate-100" data-testid={`call-item-file-${c.id}-${i}`}><FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate max-w-[200px]">{decodeURIComponent(url.split("/").pop() || "") || `Bijlage ${i + 1}`}</span></a>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <CallDetailDialog call={callDetail} onClose={() => setCallDetail(null)} photoSrc={photoSrc} onOpenPdf={openLetterPdf} />
          </Card>
        </TabsContent>

        {/* FACTORING */}
        <TabsContent value="factoring" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Factoring-facturen</h3>
              <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setFactoringOpen(true)} data-testid="add-factoring-button"><Plus className="h-4 w-4 mr-2" /> Factuur toevoegen</Button>
            </div>
            {activeFactoring().length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Nog geen actieve factoring-facturen.{archivedFactoring().length > 0 ? " Zie het tabblad Archief." : ""}</p> : (
              <div className="space-y-2">
                {activeFactoring().map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-slate-50 hover:border-slate-300 transition-colors" data-testid={`factoring-row-${f.id}`} onClick={() => setFactoringDetail(f)}>
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-2">{f.invoice_number || "—"} · {f.debtor_name}
                        {isFactoringDone(f) && <Badge className="bg-emerald-600 text-white hover:bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" /> Afgehandeld</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">Factuur {fmtDate(f.invoice_date)} • vervalt {fmtDate(f.due_date)} • <span className="text-emerald-700">klik voor details</span></div>
                    </div>
                    <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                      <div className="text-right"><div className="text-xs text-slate-400">Te ontvangen</div><div className="font-semibold">{fmtEur(f.amount)}</div></div>
                      <div className="text-right"><div className="text-xs text-slate-400">Uit te keren ({f.interest_rate}%)</div><div className="font-semibold text-emerald-700">{fmtEur(f.payout)}</div></div>
                      <div className="w-40"><Combobox testId={`factoring-status-${f.id}`} value={f.status} onChange={async (v) => { await api.put(`/factoring/${f.id}`, { ...f, status: v }); load(); }} options={[["open", "Openstaand"], ["late", "Te laat"], ["paid", "Betaald/Uitbetaald"]].map(([k, v]) => ({ value: k, label: v }))} /></div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" title="Naar archief" onClick={() => archiveFactoring(f, true)} data-testid={`factoring-tab-archive-${f.id}`}><Archive className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Verwijderen" data-testid={`factoring-tab-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Factuur verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>Deze factoring-factuur wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteFactoringItem(f)} data-testid={`factoring-tab-delete-confirm-${f.id}`}>Ja, verwijderen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TIJDSLIJN */}
        <TabsContent value="tijdslijn" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-semibold">Communicatie-tijdslijn</h3>
              <Button size="sm" variant="outline" className="text-blue-700 border-blue-200" onClick={() => downloadDossier()} data-testid="tenant-communication-pdf"><Download className="h-4 w-4 mr-2" /> Dossier (PDF)</Button>
            </div>
            {timeline.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Nog geen activiteit.</p> : (
              <div className="relative">
                {timeline.map((e, i) => {
                  const Icon = e.meta?.action_type && String(e.meta.action_type).toLowerCase().includes("klacht") ? ShieldAlert : tlIcon(e.kind);
                  return (
                    <div key={i} className="flex gap-4 pb-6 last:pb-0" data-testid={`timeline-item-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0 z-10"><Icon className="h-4 w-4 text-slate-600" /></div>
                        {i < timeline.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 mt-1" />}
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-slate-800">{e.title}</span>
                          <span className="text-xs text-slate-400">{fmtDate(e.date)}</span>
                        </div>
                        {e.description && <p className="text-sm text-slate-500 whitespace-pre-wrap mt-0.5">{e.description}</p>}
                        {e.kind === "call" && e.meta?.delivery_status && (
                          <div className="mt-2"><DeliveryBadge status={e.meta.delivery_status} channel={e.meta.delivery_channel} testId={`timeline-delivery-${i}`} /></div>
                        )}
                        {e.kind === "complaint" && (
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <DaysOpenBadge c={e.meta} />
                            <Button size="sm" variant="outline" className="h-7" onClick={() => navigate(CLOSED_COMPLAINT.includes(e.meta.status) ? "/app/archief" : "/app/klachten")} data-testid={`timeline-complaint-open-${e.meta.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> {CLOSED_COMPLAINT.includes(e.meta.status) ? "Naar archief" : "Naar klachten"}</Button>
                          </div>
                        )}
                        {e.kind === "letter" && e.meta?.id && (
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {e.meta.sent
                              ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Verzonden{e.meta.recipients?.length ? ` → ${e.meta.recipients.join(", ")}` : ""}</Badge>
                              : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Concept</Badge>}
                            <Button size="sm" variant="outline" className="h-7" onClick={() => openLetterPdf(e.meta, false)} data-testid={`timeline-letter-open-${e.meta.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Openen</Button>
                            <Button size="sm" variant="outline" className="h-7" onClick={() => openLetterPdf(e.meta, true)} data-testid={`timeline-letter-download-${e.meta.id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                          </div>
                        )}
                        {(e.attachments || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {e.attachments.map((url, j) => (
                              /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(url) ? (
                                <a key={j} href={photoSrc(url)} target="_blank" rel="noreferrer" className="block h-20 w-20 rounded-lg border overflow-hidden" data-testid={`timeline-image-${i}-${j}`}>
                                  <img src={photoSrc(url)} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform" />
                                </a>
                              ) : (
                                <a key={j} href={photoSrc(url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-slate-50 text-xs text-slate-700 hover:bg-slate-100" data-testid={`timeline-file-${i}-${j}`}><FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate max-w-[200px]">{decodeURIComponent(url.split("/").pop() || "") || `Bijlage ${j + 1}`}</span></a>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="dossiers" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-heading font-semibold flex items-center gap-2"><FolderOpen className="h-5 w-5 text-blue-700" /> Dossiers</h3>
                <p className="text-sm text-slate-500">Genummerde, samengestelde PDF-dossiers (betalingsoverzicht + tijdslijn + bijlagen).</p>
              </div>
              <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setDossierWizardOpen(true)} data-testid="dossier-new-button"><FolderPlus className="h-4 w-4 mr-2" /> Nieuw dossier</Button>
            </div>
            {dossiers.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Nog geen dossiers. Maak er één aan met de wizard.</p>
            ) : (
              <div className="space-y-2" data-testid="dossier-list">
                {dossiers.map((d) => (
                  <div key={d.id} className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border rounded-xl px-4 py-3 hover:bg-slate-50" data-testid={`dossier-item-${d.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{d.number}</Badge>
                        <span className="font-medium text-sm text-slate-800 truncate">{d.title}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {fmtDate(d.created_at)}
                        {d.totals ? ` · openstaand ${fmtEur(d.totals.outstanding)}` : ""}
                        {d.attachments?.length ? ` · ${d.attachments.length} bijlage(n)` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setViewDossier(d)} data-testid={`dossier-view-${d.id}`}><Eye className="h-4 w-4 mr-1.5" /> Bekijken</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditDossier(d)} data-testid={`dossier-edit-${d.id}`}><Pencil className="h-4 w-4 mr-1.5" /> Bewerken</Button>
                      <Button size="sm" variant="outline" onClick={() => openProgress(d)} data-testid={`dossier-progress-${d.id}`}><ClipboardList className="h-4 w-4 mr-1.5" /> Voortgang{d.entries?.length ? ` (${d.entries.length})` : ""}</Button>
                      <Button size="sm" variant="outline" onClick={() => openEmailDlg(d)} data-testid={`dossier-email-${d.id}`}><Mail className="h-4 w-4 mr-1.5" /> E-mail</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadDossierPdf(d)} data-testid={`dossier-download-${d.id}`}><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" data-testid={`dossier-delete-${d.id}`} title="Verwijderen"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Dossier {d.number} verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>Alleen dit dossier en de opgeslagen PDF worden verwijderd. De onderliggende gegevens blijven behouden. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteDossier(d)} data-testid={`dossier-delete-confirm-${d.id}`}>Ja, verwijderen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="documenten" className="mt-4">
          {tab === "documenten" && <><ClientAgreements tenantId={id} hideEmpty /><div className="mt-4"><DocumentsTab tenantId={id} onChanged={load} /></div></>}
        </TabsContent>

        <TabsContent value="archief" className="mt-4">
          <Card className="p-6 space-y-6" data-testid="archief-content">
            <div>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-1"><Archive className="h-5 w-5 text-slate-600" /> Archief</h3>
              <p className="text-sm text-slate-500">Afgehandelde en gearchiveerde leningen/contracten en facturen. U kunt ze terugzetten of definitief verwijderen.</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-600 mb-2">Overeenkomsten / leningen</h4>
              {archivedContracts().length === 0 ? <p className="text-sm text-slate-400">Geen gearchiveerde overeenkomsten.</p> : (
                <div className="space-y-2">
                  {archivedContracts().map((c) => {
                    const kindLabel = (k) => k === "lening" ? "Lening" : k === "factuur" ? "Factuur" : k === "overig" ? "Overig" : "Huur";
                    return (
                      <div key={c.id} className="flex items-center justify-between border rounded-lg px-4 py-3" data-testid={`archived-contract-${c.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{kindLabel(c.kind)}</Badge>
                          {c.ovk_number && <span className="text-xs font-mono text-slate-400">{c.ovk_number}</span>}
                          <span className="text-sm text-slate-600 truncate">{fmtEur(c.kind === "lening" ? c.total_amount : c.monthly_rent)} · gearchiveerd {fmtDate(c.archived_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => archiveContract(c, false)} data-testid={`restore-contract-${c.id}`}><ArchiveRestore className="h-4 w-4 mr-1.5" /> Terugzetten</Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" title="Definitief verwijderen" data-testid={`archived-contract-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{kindLabel(c.kind)} definitief verwijderen?</AlertDialogTitle>
                                <AlertDialogDescription>Deze overeenkomst én alle bijbehorende betaaltermijnen worden definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteContract(c)} data-testid={`archived-contract-delete-confirm-${c.id}`}>Ja, verwijderen</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-600 mb-2">Factoring-facturen</h4>
              {archivedFactoring().length === 0 ? <p className="text-sm text-slate-400">Geen gearchiveerde facturen.</p> : (
                <div className="space-y-2">
                  {archivedFactoring().map((f) => (
                    <div key={f.id} className="flex items-center justify-between border rounded-lg px-4 py-3" data-testid={`archived-factoring-${f.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{f.invoice_number || "—"} · {f.debtor_name}</div>
                        <div className="text-xs text-slate-400">{fmtEur(f.amount)} · gearchiveerd {fmtDate(f.archived_at)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => archiveFactoring(f, false)} data-testid={`restore-factoring-${f.id}`}><ArchiveRestore className="h-4 w-4 mr-1.5" /> Terugzetten</Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" title="Definitief verwijderen" data-testid={`archived-factoring-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Factuur definitief verwijderen?</AlertDialogTitle>
                              <AlertDialogDescription>Deze factoring-factuur wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteFactoringItem(f)} data-testid={`archived-factoring-delete-confirm-${f.id}`}>Ja, verwijderen</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} tenantId={id} />
      <NotifySettingsWizard open={notifyOpen} onOpenChange={setNotifyOpen} tenant={t} onSaved={load} />
      {(topupContract || t.contract) && <TopupWizard open={topupOpen} onOpenChange={setTopupOpen} contract={topupContract || t.contract} onSaved={load} />}
      <TenantReminderDialog open={reminderOpen} onOpenChange={setReminderOpen} tenantId={t.id} />
      <AddTermWizard open={addTermOpen} onOpenChange={setAddTermOpen} tenantId={id} contract={t.contract} contracts={t.contracts || (t.contract ? [t.contract] : [])} onSaved={load} />
      <StatementDialog open={statementOpen} onOpenChange={setStatementOpen} tenantId={id} contracts={t.contracts || (t.contract ? [t.contract] : [])} />
      <ContractWizard open={contractOpen} onOpenChange={setContractOpen} tenantId={id} tenant={t} onSaved={load} />
      <TenantEditDialog open={editOpen} onOpenChange={setEditOpen} tenant={t} onSaved={load} />
      <FactoringWizard open={factoringOpen} onOpenChange={setFactoringOpen} tenantId={id} onSaved={load} />
      <ProcessPaymentWizard open={processOpen} onOpenChange={(o) => { setProcessOpen(o); if (!o) setProcessPaymentId(null); }} tenantId={id} payments={payments} presetPaymentId={processPaymentId} onSaved={load} />
      <FactoringDetailDialog open={!!factoringDetail} onOpenChange={(o) => !o && setFactoringDetail(null)} factoring={factoringDetail} onSaved={() => { load(); }} />

      <QuickMessageDialog open={msgDialog.open} onOpenChange={(o) => setMsgDialog((s) => ({ ...s, open: o }))} contacts={msgDialog.contacts} templates={msgTemplates()} defaultTemplate={defaultMsgTemplate()} logPayload={{ tenant_id: id }} onLogged={load} />
      <DossierWizard open={dossierWizardOpen} onOpenChange={setDossierWizardOpen} tenantId={id} contracts={t.contracts || (t.contract ? [t.contract] : [])} onSaved={loadDossiers} />

      <DossierViewer dossier={viewDossier} onClose={() => setViewDossier(null)} />
      <DossierEditDialog dossier={editDossier} onClose={() => setEditDossier(null)} onSaved={loadDossiers} />
      <Dialog open={progressDlg.open} onOpenChange={(o) => setProgressDlg((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dossier-progress-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-blue-700" /> Voortgang · {progressDlg.dossier?.number}</DialogTitle>
            <DialogDescription>Voeg gespreksnotities of losse bestanden toe om de voortgang van deze zaak te volgen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea rows={3} value={progressDlg.note} onChange={(e) => setProgressDlg((s) => ({ ...s, note: e.target.value }))} placeholder="Nieuwe gespreksnotitie / update…" data-testid="dossier-progress-note" />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={addProgressNote} disabled={progressDlg.busy} className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" data-testid="dossier-progress-add-note"><Plus className="h-4 w-4 mr-1.5" /> Notitie toevoegen</Button>
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 border rounded-lg px-3 py-1.5 cursor-pointer hover:bg-slate-50" data-testid="dossier-progress-upload-label">
                <Upload className="h-4 w-4" /> Bestand
                <input type="file" multiple className="hidden" onChange={(e) => uploadProgressFiles(Array.from(e.target.files || []))} data-testid="dossier-progress-upload-input" />
              </label>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-2" data-testid="dossier-followup-box">
              <div className="text-sm font-medium text-slate-700">Opvolgtaak plannen</div>
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2">
                <DateField testId="dossier-followup-date" value={fu.date} onChange={(v) => setFu((s) => ({ ...s, date: v }))} />
                <Input value={fu.note} onChange={(e) => setFu((s) => ({ ...s, note: e.target.value }))} placeholder="Wat moet er gebeuren? (optioneel)" data-testid="dossier-followup-note" />
              </div>
              <Button size="sm" variant="outline" onClick={addDossierFollowup} data-testid="dossier-followup-add"><CalendarClock className="h-4 w-4 mr-1.5" /> Opvolgtaak toevoegen</Button>
            </div>
            <div className="border-t pt-3 space-y-2" data-testid="dossier-progress-list">
              {(!progressDlg.dossier?.entries || progressDlg.dossier.entries.length === 0) ? (
                <p className="text-sm text-slate-400 text-center py-4">Nog geen voortgang vastgelegd.</p>
              ) : (
                [...progressDlg.dossier.entries].reverse().map((e) => (
                  <div key={e.id} className="flex items-start justify-between gap-2 border rounded-lg px-3 py-2" data-testid={`dossier-progress-entry-${e.id}`}>
                    <div className="min-w-0 text-sm">
                      {e.type === "file" ? (
                        <a href={e.file_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-700 hover:underline"><FileText className="h-4 w-4" /> {e.file_name || "Bestand"}</a>
                      ) : (
                        <div className="text-slate-700 whitespace-pre-wrap">{e.text}</div>
                      )}
                      <div className="text-xs text-slate-400 mt-0.5">{fmtDate(e.at)} · {e.by}</div>
                    </div>
                    <button className="text-slate-300 hover:text-red-600 shrink-0" onClick={() => deleteProgressEntry(e.id)} data-testid={`dossier-progress-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={emailDlg.open} onOpenChange={(o) => setEmailDlg((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="dossier-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-blue-700" /> Dossier e-mailen</DialogTitle>
            <DialogDescription>Verstuur {emailDlg.dossier?.number} als PDF-bijlage naar de opdrachtgever of debiteur.</DialogDescription>
          </DialogHeader>
          {!emailDlg.recipients ? (
            <p className="text-sm text-slate-400 py-4">Ontvangers laden…</p>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Ontvanger</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[{ k: "tenant", lbl: "Debiteur" }, { k: "client", lbl: "Opdrachtgever" }].map(({ k, lbl }) => {
                    const emails = emailDlg.recipients[k]?.emails || [];
                    return (
                      <button key={k} disabled={emails.length === 0}
                        onClick={() => setEmailDlg((s) => ({ ...s, type: k, email: emails[0] || "" }))}
                        className={`text-left border rounded-lg px-3 py-2 text-sm ${emailDlg.type === k ? "border-blue-600 bg-blue-50" : "border-slate-200"} ${emails.length === 0 ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"}`}
                        data-testid={`dossier-email-type-${k}`}>
                        <div className="font-medium">{lbl}</div>
                        <div className="text-xs text-slate-400 truncate">{emailDlg.recipients[k]?.name || "—"}{emails.length === 0 ? " · geen e-mail" : ""}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">E-mailadres</Label>
                <Combobox testId="dossier-email-select" value={emailDlg.email} onChange={(v) => setEmailDlg((s) => ({ ...s, email: v }))} placeholder="Kies e-mailadres" searchPlaceholder="Zoek e-mail…" options={(emailDlg.recipients[emailDlg.type]?.emails || []).map((e) => ({ value: e, label: e }))} />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-500">Bericht (optioneel)</Label>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-violet-700 hover:bg-violet-50" disabled={emailDlg.aiBusy} onClick={aiDossierEmail} data-testid="dossier-email-ai"><Sparkles className="h-3.5 w-3.5 mr-1" /> {emailDlg.aiBusy ? "Bezig…" : "AI-tekst"}</Button>
                </div>
                <Textarea rows={5} value={emailDlg.message} onChange={(e) => setEmailDlg((s) => ({ ...s, message: e.target.value }))} placeholder="Korte begeleidende tekst in de e-mail" data-testid="dossier-email-message" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailDlg((s) => ({ ...s, open: false }))}>Annuleren</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={sendDossierEmail} disabled={emailDlg.sending || !emailDlg.email} data-testid="dossier-email-send"><Mail className="h-4 w-4 mr-2" /> {emailDlg.sending ? "Verzenden…" : "Verstuur"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DossierExportDialog open={dossierOpen} onOpenChange={setDossierOpen} endpoint={`/tenants/${id}/communication/pdf`} filename={`communicatie_${t.last_name || id}.pdf`} kinds={[{ value: "call", label: "Gesprekken/notities" }, { value: "letter", label: "Brieven" }, { value: "payment", label: "Betalingen" }, { value: "payout", label: "Uitbetalingen" }]} />

      <Dialog open={signDialog.open} onOpenChange={(o) => !o && setSignDialog({ open: false, mode: null, agreement: null })}>
        <DialogContent data-testid="sign-email-dialog">
          <DialogHeader>
            <DialogTitle>Ondertekenlink versturen</DialogTitle>
            <DialogDescription>Kies naar welk(e) e-mailadres(sen) de ondertekenlink wordt gestuurd.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {savedEmails().length > 0 ? (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Opgeslagen e-mailadressen</Label>
                {savedEmails().map((e) => (
                  <label key={e} className="flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 hover:bg-slate-50" data-testid={`sign-email-option-${e}`}>
                    <Checkbox checked={signEmails.includes(e)} onCheckedChange={() => toggleSignEmail(e)} data-testid={`sign-email-check-${e}`} />
                    <span className="text-slate-700">{e}</span>
                  </label>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">Geen opgeslagen e-mailadressen bij deze debiteur.</p>}
            <div>
              <Label className="text-xs text-slate-500">Ander e-mailadres (optioneel, komma-gescheiden)</Label>
              <Input value={signExtra} onChange={(e) => setSignExtra(e.target.value)} placeholder="naam@voorbeeld.nl" data-testid="sign-email-extra-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialog({ open: false, mode: null, agreement: null })} data-testid="sign-email-cancel">Annuleren</Button>
            <Button className="bg-[#C9A227] hover:bg-[#B8921F] text-[#0F1E3D]" onClick={confirmSign} data-testid="sign-email-confirm"><Mail className="h-4 w-4 mr-2" /> Versturen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
